import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FileQuestion, ServerCrash } from 'lucide-react';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageContainer,
  PageHeader,
  UnauthorizedState,
  buttonClass,
} from '@castinflow/ui';
import { ApiError } from '../lib/api';
import { usePageMeta } from '../shell/page-meta';
import { tr } from '../i18n/tr';

/** Kabuk içi "bulunamadı" (yalnız oturum açık kullanıcı; oturum dışı bilinmeyen rota girişe yönlenir). */
export function NotFoundPage() {
  usePageMeta(tr.states.notFoundTitle);
  return (
    <PageContainer>
      <PageHeader title={tr.states.notFoundTitle} />
      {/* v3.5: hata kodu dekoratif büyük rakamla (yönetim paneli 404 deseni); anlam başlık ve açıklamada */}
      <Card className="mx-auto max-w-2xl">
        <p
          aria-hidden="true"
          className="pt-10 text-center text-[64px] font-semibold leading-none tracking-tight text-border-strong"
        >
          404
        </p>
        <EmptyState
          testId="not-found"
          icon={<FileQuestion size={22} />}
          title={tr.states.notFoundHeading}
          description={tr.states.notFoundBody}
          action={
            <Link to="/" className={buttonClass('primary')}>
              {tr.states.backHome}
            </Link>
          }
        />
      </Card>
    </PageContainer>
  );
}

/**
 * Yetkisiz: arayüz güvenlik sınırı değildir; bu yüzey hem menüde olmayan rotaya doğrudan gidildiğinde hem de API 403
 * döndüğünde gösterilir. Kaynağın varlığı hakkında bilgi vermez.
 */
export function UnauthorizedContent() {
  return (
    <Card>
      <UnauthorizedState
        testId="unauthorized"
        title={tr.states.unauthorizedTitle}
        description={tr.states.unauthorizedBody}
        action={
          <Link to="/" className={buttonClass('secondary')}>
            {tr.states.backHome}
          </Link>
        }
      />
    </Card>
  );
}

export function UnauthorizedPage({ title }: { title: string }) {
  usePageMeta(title);
  return (
    <PageContainer>
      <PageHeader title={title} />
      <UnauthorizedContent />
    </PageContainer>
  );
}

/** Sorgu hatası için kullanıcı dili: 403 → yetkisiz, diğerleri → yeniden dene (teknik ayrıntı gösterilmez). */
export function QueryErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (error instanceof ApiError && (error.status === 403 || error.code === 'FORBIDDEN')) {
    return <UnauthorizedContent />;
  }
  return (
    <ErrorState
      testId="load-error"
      headingLevel={3}
      title={tr.states.loadErrorTitle}
      description={tr.states.loadErrorBody}
      action={<Button onClick={onRetry}>{tr.states.retry}</Button>}
    />
  );
}

/** Beklenmeyen istemci hatası: kullanıcıya teknik metin gösterilmez; ayrıntı yalnız konsola (hassas veri içermez). */
export class SystemErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      'Beklenmeyen arayüz hatası',
      error.name,
      info.componentStack?.split('\n')[1]?.trim(),
    );
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="min-h-screen bg-canvas">
        <PageContainer width="form">
          <Card>
            <ErrorState
              testId="system-error"
              icon={<ServerCrash size={18} />}
              title={tr.states.systemErrorTitle}
              description={tr.states.systemErrorBody}
              action={
                <Button
                  variant="primary"
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  {tr.states.reload}
                </Button>
              }
            />
          </Card>
        </PageContainer>
      </div>
    );
  }
}
